'use strict';


import {
	Color,
	FloatType,
	LinearFilter, LinearSRGBColorSpace,
	Mesh,
	MeshBasicMaterial,
	NearestFilter,
	RGBAFormat,
	SphereGeometry, Vector2, Vector3,
	WebGLRenderTarget,
} from 'three';
import { Buildings, projectMode, threeJsVariables } from '../../../../settings';
import { TilesPluginCore } from './TilesPluginCore';

const _color = new Color();

export class BuildingsPlugin extends TilesPluginCore{
	constructor (panorama) {
		super('BuildingsPlugin');
		
		this.panorama = panorama;
		this.camera = this.panorama.camera;
		this.renderer = this.panorama.renderer;
		this.selectedBuilding = null;
		this.renderSize = new Vector2();
		this.renderer.getSize(this.renderSize);
		this.mapping = [];
		
		this.pointer = {
			down: new Vector2(),
			up: new Vector2(),
			isDown: false
		};
		
		this.colorBuffer = new Float32Array(4);
		this.pixel = new Vector2();
		
		const geometry = new SphereGeometry(this.panorama.config.size,96,96);
		geometry.scale(-1,1,1);
		const material = new MeshBasicMaterial({map: this.panorama.mask.buildings});
		this.mesh = new Mesh(geometry,material);
		this.mesh.name = 'BuildingsPanorama';
		this.mesh.visible = false;
		
		this.rt = new WebGLRenderTarget(this.renderer.domElement.width,this.renderer.domElement.height,{
			minFilter: LinearFilter,
			magFilter: NearestFilter,
			format: RGBAFormat,
			type: FloatType
		});
		
		this.panorama.add(this.mesh);
		
		this.onPointerDown = this.#onPointerDown.bind(this);
		this.onPointerUp = this.#onPointerUp.bind(this);
		this.onPointerMove = this.#onPointerMove.bind(this);
		
		this.panorama.container.addEventListener('pointerdown',this.onPointerDown);
		this.panorama.container.addEventListener('pointermove',this.onPointerMove);
		this.panorama.container.addEventListener('pointerup',this.onPointerUp);
		
		this.#parseMapping();
	}
	
	#onPointerDown(e) {
		if (!this.panorama.enabled)
			return;
		
		this.pointer.down.set(e.pageX,e.pageY);
		this.pointer.isDown = true;
	}
	
	#onPointerUp(e) {
		if (!this.panorama.enabled)
			return;
		
		this.pointer.up.set(e.pageX,e.pageY);
		this.pointer.isDown = false;
		
		if (this.pointer.down.equals(this.pointer.up)) {
			this.intersects(threeJsVariables.mouse.x,threeJsVariables.mouse.y);
			this.#click();
		}
	}
	
	#onPointerMove(e) {
		if (!this.panorama.enabled || e.pointerType === 'touch' || this.pointer.isDown)
			return;
		
		this.intersects(threeJsVariables.mouse.x,threeJsVariables.mouse.y);
	}
	
	intersects(x,y) {
		// const x = (pageX / this.renderSize.x) * 2 - 1;
		// const y = -(pageY / this.renderSize.y) * 2 + 1;
		
		this.pixel.set(
			this.renderer.domElement.width / 2 + x * this.renderer.domElement.width / 2,
			this.renderer.domElement.height / 2 + y * this.renderer.domElement.height / 2
		);
		this.renderer.setRenderTarget(this.rt);
		this.mesh.visible = true;
		this.renderer.render(this.mesh,this.panorama.camera);
		this.renderer.setRenderTarget(null);
		this.mesh.visible = false;
		
		this.renderer.readRenderTargetPixels(this.rt,this.pixel.x,this.pixel.y,1,1,this.colorBuffer);
		
		if (this.colorBuffer[0] !== 0 || this.colorBuffer[1] !== 0 || this.colorBuffer[2] !== 0) {
			_color.set(this.colorBuffer[0],this.colorBuffer[1],this.colorBuffer[2]);
			this.#hover(_color);
		} else {
			this.#hoverOut();
		}
	}
	
	#hover(color) {
		this.selectedBuilding = this.#findSelected(color);
		if (this.selectedBuilding) {
			this.renderer.domElement.style.cursor = 'pointer';
			
			for (let i = 0; i < this.panorama.tiles.length; i++) {
				this.panorama.tiles[i].mesh.material.uniforms.intersectedColor.value.copy(color);
				this.panorama.tiles[i].mesh.material.uniforms.isIntersected.value = true;
			}
			
			const labelPlugin = this.panorama.getPlugin('BuildingLabelsPlugin');
			if (labelPlugin) {
				labelPlugin.hover(this.selectedBuilding.buildingEngineId);
			}
		}
	}
	
	#hoverOut() {
		this.selectedBuilding = null;
		this.renderer.domElement.style.cursor = '';
		for (let i = 0; i < this.panorama.tiles.length; i++) {
			this.panorama.tiles[i].mesh.material.uniforms.isIntersected.value = false;
		}
		const labelPlugin = this.panorama.getPlugin('BuildingLabelsPlugin');
		labelPlugin && labelPlugin.hoverOut();
	}
	
	#click() {
		if (this.selectedBuilding) {
			const building = Buildings.getBuilding(this.selectedBuilding.buildingEngineId);
			projectMode.Cylinder.enter(building);
			this.#hoverOut();
		}
	}
	
	#calculateDistance(c1,c2) {
		return Math.sqrt((c1.r - c2.r) ** 2 + (c1.g - c2.g) ** 2 + (c1.b - c2.b) ** 2);
	}
	
	#parseMapping() {
		this.panorama.config.mapping.forEach(data => {
			const color = new Color().fromArray(data.color);
			this.mapping.push({buildingEngineId: data.buildingEngineId, color: color});
		});
	}
	
	#findSelected(color) {
		const distances = this.mapping.map(data => this.#calculateDistance(data.color,color));
		const min = Math.min(...distances);
		if (min < 0.05) {
			const index = distances.indexOf(min);
			return this.mapping[index];
		}
		return  null;
	}
	
	resize() {
		if (this.rt) {
			this.renderer.getSize(this.renderSize);
			this.rt.setSize(this.renderer.domElement.width,this.renderer.domElement.height);
		}
	}
	
	dispose() {
		this.mesh.removeFromParent();
		this.mesh.geometry.dispose();
		this.mesh.material.map.dispose();
		this.mesh.material.dispose();
		this.mesh = null;
		
		this.panorama.container.removeEventListener('pointerdown',this.onPointerDown);
		this.panorama.container.removeEventListener('pointermove',this.onPointerMove);
		this.panorama.container.removeEventListener('pointerup',this.onPointerUp);
		
		this.onPointerDown = null;
		this.onPointerUp = null;
		this.onPointerMove = null;
		
		this.rt.dispose();
		this.rt = null;
		this.colorBuffer = null;
		this.pixel = null;
	}
	
}
